import { AsyncCreatableSelect } from '../AsyncSelect/AsyncCreatableSelect';
import { AsyncSelect } from '../AsyncSelect/AsyncSelect';
import {
  type AsyncCreatableSelectProps,
  type AsyncSelectProps,
} from '../AsyncSelect/types';
import { Select } from '../Select/Select';
import { type SelectProps } from '../Select/types';

import { type FieldErrorContext, type FieldErrorSource } from './fieldErrors';
import {
  type FieldErrorDisplay,
  FieldShell,
  getFieldDescriptionIds,
} from './FieldShell';
import { useFieldContext } from './form-context';
import { useFieldChrome } from './useFieldChrome';

type SelectFieldValue = string | null | string[];

type DistributiveOmit<T, TKey extends PropertyKey> = T extends unknown
  ? Omit<T, TKey>
  : never;

type FieldControlledProp = 'aria-describedby' | 'errors' | 'onChange' | 'value';

type FieldChromeProps = {
  readonly errorDisplay?: FieldErrorDisplay;
  readonly errorSource?: FieldErrorSource;
  readonly externalErrors?: readonly unknown[];
  readonly fieldClassName?: string;
  readonly fieldStyle?: ComponentStyling['style'];
  readonly hint?: string;
  readonly label?: string;
  readonly mapErrors?: (
    context: FieldErrorContext,
  ) => readonly string[] | undefined;
};

type StaticSelectFieldProps<TItem> = Omit<
  SelectProps<TItem>,
  FieldControlledProp | 'isMultiSelect'
> & {
  readonly isMulti?: boolean;
};

type AsyncSelectFieldProps<TItem, TPageParam> = DistributiveOmit<
  AsyncSelectProps<TItem, TPageParam>,
  FieldControlledProp
> & {
  readonly isCreatable?: false;
};

type AsyncCreatableSelectFieldProps<TItem, TPageParam> = DistributiveOmit<
  AsyncCreatableSelectProps<TItem, TPageParam>,
  FieldControlledProp
> & {
  readonly isCreatable: true;
};

type SelectFieldControlProps<TItem, TPageParam> =
  | StaticSelectFieldProps<TItem>
  | AsyncSelectFieldProps<TItem, TPageParam>
  | AsyncCreatableSelectFieldProps<TItem, TPageParam>;

export type SelectFieldProps<TItem, TPageParam = number> = FieldChromeProps &
  SelectFieldControlProps<TItem, TPageParam>;

type AnyAsyncSelectFieldProps<TItem, TPageParam> =
  | AsyncSelectFieldProps<TItem, TPageParam>
  | AsyncCreatableSelectFieldProps<TItem, TPageParam>;

function hasDataSource<TItem, TPageParam>(
  props: SelectFieldControlProps<TItem, TPageParam>,
): props is AnyAsyncSelectFieldProps<TItem, TPageParam> {
  return 'dataSource' in props;
}

type SelectFieldChrome = ReturnType<typeof useFieldChrome>;

function SelectFieldControl<TItem, TPageParam>({
  ariaDescribedBy,
  chrome,
  props,
}: {
  ariaDescribedBy: string | undefined;
  chrome: SelectFieldChrome;
  props: SelectFieldControlProps<TItem, TPageParam>;
}) {
  const field = useFieldContext<SelectFieldValue>();

  if (hasDataSource(props)) {
    return (
      <AsyncSelectFieldControl
        ariaDescribedBy={ariaDescribedBy}
        chrome={chrome}
        field={field}
        props={props}
      />
    );
  }

  return (
    <StaticSelectFieldControl
      ariaDescribedBy={ariaDescribedBy}
      chrome={chrome}
      field={field}
      props={props}
    />
  );
}

function AsyncSelectFieldControl<TItem, TPageParam>({
  ariaDescribedBy,
  chrome,
  field,
  props,
}: {
  ariaDescribedBy: string | undefined;
  chrome: SelectFieldChrome;
  field: ReturnType<typeof useFieldContext<SelectFieldValue>>;
  props: AnyAsyncSelectFieldProps<TItem, TPageParam>;
}) {
  const { isCreatable, isMulti, ...controlProps } = props;
  const sharedProps = {
    ...controlProps,
    'aria-describedby': ariaDescribedBy,
    dataSource: props.dataSource,
    errors: chrome.errors,
    inputId: chrome.inputId,
  };

  return isMulti ? (
    <AsyncMultiSelectField
      field={field}
      isCreatable={isCreatable}
      props={sharedProps}
    />
  ) : (
    <AsyncSingleSelectField
      field={field}
      isCreatable={isCreatable}
      props={sharedProps}
    />
  );
}

function AsyncMultiSelectField<TItem, TPageParam>({
  field,
  isCreatable,
  props,
}: {
  field: ReturnType<typeof useFieldContext<SelectFieldValue>>;
  isCreatable?: boolean;
  props: Omit<AnyAsyncSelectFieldProps<TItem, TPageParam>, 'isMulti'>;
}) {
  const { dataSource, ...selectProps } = props;
  const valueProps = {
    isMulti: true as const,
    value: Array.isArray(field.state.value) ? field.state.value : [],
    onChange: (value: string[]) => {
      field.handleChange(value);
    },
  };

  if (isCreatable) {
    if (dataSource.isPaginated) {
      throw new Error('Async creatable selects do not support pagination.');
    }

    return (
      <AsyncCreatableSelect<TItem, TPageParam>
        {...selectProps}
        {...valueProps}
        dataSource={dataSource}
      />
    );
  }

  return (
    <AsyncSelect<TItem, TPageParam>
      {...selectProps}
      {...valueProps}
      dataSource={dataSource}
    />
  );
}

function AsyncSingleSelectField<TItem, TPageParam>({
  field,
  isCreatable,
  props,
}: {
  field: ReturnType<typeof useFieldContext<SelectFieldValue>>;
  isCreatable?: boolean;
  props: Omit<AnyAsyncSelectFieldProps<TItem, TPageParam>, 'isMulti'>;
}) {
  const { dataSource, ...selectProps } = props;
  const value = Array.isArray(field.state.value)
    ? (field.state.value[0] ?? null)
    : field.state.value;
  const valueProps = {
    isMulti: false as const,
    value,
    onChange: (nextValue: string | null) => {
      field.handleChange(nextValue || null);
    },
  };

  if (isCreatable) {
    if (dataSource.isPaginated) {
      throw new Error(
        'Async creatable selects for pagination is currently not supported .',
      );
    }

    return (
      <AsyncCreatableSelect<TItem, TPageParam>
        {...selectProps}
        {...valueProps}
        dataSource={dataSource}
      />
    );
  }

  return (
    <AsyncSelect<TItem, TPageParam>
      {...selectProps}
      {...valueProps}
      dataSource={dataSource}
    />
  );
}

function StaticSelectFieldControl<TItem>({
  ariaDescribedBy,
  chrome,
  field,
  props,
}: {
  ariaDescribedBy: string | undefined;
  chrome: SelectFieldChrome;
  field: ReturnType<typeof useFieldContext<SelectFieldValue>>;
  props: StaticSelectFieldProps<TItem>;
}) {
  const { isMulti, ...controlProps } = props;

  return (
    <Select<TItem>
      {...controlProps}
      aria-describedby={ariaDescribedBy}
      errors={chrome.errors.join(', ')}
      inputId={chrome.inputId}
      isMultiSelect={isMulti}
      value={
        isMulti
          ? Array.isArray(field.state.value)
            ? field.state.value
            : []
          : Array.isArray(field.state.value)
            ? (field.state.value[0] ?? '')
            : (field.state.value ?? '')
      }
      onChange={(value) => {
        field.handleChange(
          isMulti
            ? Array.isArray(value)
              ? value
              : []
            : Array.isArray(value)
              ? (value[0] ?? null)
              : value || null,
        );
      }}
    />
  );
}

/**
 * TanStack Form adapter for static, async, and async-creatable selects.
 */
export function SelectField<TItem, TPageParam = number>(
  props: SelectFieldProps<TItem, TPageParam>,
) {
  const {
    errorDisplay,
    errorSource,
    externalErrors,
    fieldClassName,
    fieldStyle,
    hint,
    inputId,
    label,
    mapErrors,
    ...controlProps
  } = props;
  const field = useFieldContext<SelectFieldValue>();
  const chrome = useFieldChrome({
    errorSource,
    externalErrors,
    inputId,
    mapErrors,
  });
  const ariaDescribedBy = getFieldDescriptionIds({
    hasErrors: chrome.hasErrors && errorDisplay !== 'none',
    hasHint: Boolean(hint),
    inputId: chrome.inputId,
  });

  return (
    <FieldShell
      className={fieldClassName}
      errorDisplay={errorDisplay}
      errors={chrome.errors}
      hint={hint}
      inputId={chrome.inputId}
      isRequired={chrome.isRequired}
      label={label}
      style={fieldStyle}
    >
      <div onBlurCapture={field.handleBlur}>
        <SelectFieldControl
          ariaDescribedBy={ariaDescribedBy}
          chrome={chrome}
          props={controlProps}
        />
      </div>
    </FieldShell>
  );
}
